import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LoginModeResponse } from '@asset-metrix/portal-auth';
import {
  LoginWidget,
  ResetPasswordConfirmationWidget,
  ResetPasswordWidget,
} from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import {
  isAbortError,
  isPortalAuthNetworkError,
  isPortalAuthServiceUnavailableError,
} from '../api/errors';
import { setLoginModeData } from '../api/portalApi/loginMode';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStore } from '../store';
import { extractErrorMessage } from '../utils/errorMessage';
import { redirectToSsoLogin, redirectToSsoResetPwUrl } from '../utils/sso';

import styles from './login.module.scss';

export const Login = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loginMode, setLoginMode] =
    useState<LoginModeResponse['mode']>('local');
  const [loginState, setLoginState] = useState<
    'login' | 'reset' | 'reset_sent'
  >('login');
  const [loginError, setLoginError] = useState<string | undefined>();
  // Error boundary will not catch asynchronous errors and errors from event handlers, so we need to go through a useState/useEffect here
  const [fatalLoginError, setFatalLoginError] = useState<Error | undefined>();
  const [resetPasswordError, setResetPasswordError] = useState<
    string | undefined
  >();
  const loginModeAbortController = useRef<AbortController>(null);

  const [initialLoginModeData, loginSession, session, isLoggedIn] = useStore(
    useShallow((state) => [
      state.initialLoginModeData,
      state.loginSession,
      state.session,
      state.isSessionAuthenticated() && !state.isSessionExpired(),
    ])
  );

  useDocumentTitle('Login');

  useEffect(() => {
    if (fatalLoginError) {
      throw fatalLoginError;
    }
  }, [fatalLoginError]);

  const checkLoginMode = useCallback(
    async (email: string) => {
      setLoginError(undefined);
      try {
        if (loginModeAbortController.current !== null) {
          loginModeAbortController.current.abort();
        }
        loginModeAbortController.current = new AbortController();
        const resp = await session?.getLoginMode(
          email,
          loginModeAbortController.current.signal
        );
        loginModeAbortController.current = null;

        if (resp?.mode) {
          setLoginMode(resp?.mode);
          // update query cache
          setLoginModeData(resp);
        }
      } catch (error: unknown) {
        if (isAbortError(error)) {
          return;
        } else if (
          isPortalAuthServiceUnavailableError(error) ||
          isPortalAuthNetworkError(error)
        ) {
          setFatalLoginError(error);
        } else {
          setLoginError(extractErrorMessage(error, 'Login mode not available'));
        }
      }
    },
    [session]
  );

  async function onLogin(email: string, password: string) {
    setLoginError(undefined);
    try {
      switch (loginMode) {
        case 'sso': {
          // The identity provider owns the password, so there is no
          // change-password step. The return URL is applied by SSO.
          const redirectUrl = session?.getRedirectUrl({ clear: true });

          redirectToSsoLogin({
            email,
            redirect_uri: redirectUrl,
          });
          break;
        }
        case 'local': {
          await loginSession(email, password);
          // Leave the stored return URL in place until the password is changed.
          // Following it now reloads that page, which sends the user back here.
          if (session?.hasToChangePassword()) {
            navigate('/changeInitialPassword');
            break;
          }
          const redirectUrl = session?.getRedirectUrl({ clear: true });
          if (redirectUrl) {
            // Need to use basic browser functionality, because redirect url includes e.g. the router basename
            globalThis.location.assign(redirectUrl);
          } else {
            navigate('/');
          }
          break;
        }
        case 'multi':
          break;
        default:
          return setLoginError('Unknown login mode detected.');
      }
    } catch (error: unknown) {
      if (
        isPortalAuthServiceUnavailableError(error) ||
        isPortalAuthNetworkError(error)
      ) {
        setFatalLoginError(error);
      } else {
        setLoginError(extractErrorMessage(error, 'Login failed'));
      }
    }
  }

  function onResetPassword(email: string) {
    setResetPasswordError(undefined);
    if (loginMode === 'sso') {
      redirectToSsoResetPwUrl(email);
    } else {
      setLoginState('reset');
    }
  }

  async function onSubmitResetPassword(email: string) {
    if (loginMode === 'sso') {
      redirectToSsoResetPwUrl(email);
    } else {
      try {
        await session?.resetPassword(email);
        setLoginState('reset_sent');
      } catch (error: unknown) {
        setResetPasswordError(
          extractErrorMessage(error, 'Reset password failed')
        );
      }
    }
  }

  // Redirect logged-in users to home page, unless they still have to change
  // their password. The stored return URL is applied after that change.
  if (isLoggedIn) {
    return (
      <Navigate
        to={session?.hasToChangePassword() ? '/changeInitialPassword' : '/'}
        replace
      />
    );
  }

  if (initialLoginModeData?.mode === 'sso') {
    // The identity provider owns the password, so there is no
    // change-password step. The return URL is applied by SSO.
    const redirectUrl = session?.getRedirectUrl({ clear: true });

    redirectToSsoLogin({
      redirect_uri: redirectUrl,
    });
    return;
  }

  function renderWidget() {
    switch (loginState) {
      case 'reset':
        return (
          <ResetPasswordWidget
            isSso={loginMode === 'sso'}
            onBackToLogin={() => setLoginState('login')}
            onEnterValidEmail={
              initialLoginModeData?.mode === 'multi'
                ? checkLoginMode
                : undefined
            }
            onSubmit={onSubmitResetPassword}
            error={resetPasswordError}
          />
        );
      case 'reset_sent':
        return (
          <ResetPasswordConfirmationWidget
            onBackToLogin={() => setLoginState('login')}
          />
        );
      default:
        return (
          <LoginWidget
            introductionText={state && state.logoutReason}
            isSso={loginMode === 'sso'}
            loginError={loginError}
            onEnterValidEmail={
              initialLoginModeData?.mode === 'multi'
                ? checkLoginMode
                : undefined
            }
            onLoginSubmit={onLogin}
            onResetPassword={onResetPassword}
          />
        );
    }
  }

  return (
    <div className={styles['login__root']}>
      <div className={styles['login__tenant-logo']} />
      {renderWidget()}
    </div>
  );
};
